import { useCallback, useEffect, useState } from 'react';
import { QueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { ApiService } from 'api/services/ApiService';
import { listWorkboardDirectories } from 'dashboard409a/services/dashboard';
import type { IWorkboardDirectory } from 'dashboard409a/services/types';
import { Logger } from 'utilities';
import { DEFAULT_DROPBOX_ROOT, DROPBOX_FOLDER_STATUS } from './dropbox.constants';
import type { DropboxFolderOption, DropboxFolderStatus } from './dropbox.types';
import { getDropboxFolderMatchKind, normalizeFolderSegment } from './dropbox.utils';
import { DROPBOX_FOLDER_SYNC_MESSAGES, DROPBOX_FOLDER_SYNC_QUERY_KEYS } from './useDropboxFolderSync.constants';
import {
  dropboxRootFromReference,
  normalizeCmdFileStorageReference,
  toWorkboardRelativePrefixFromStorageReference,
  valuationFolderNameFromWorkboardPath,
  verifyBackupFolderExists,
  workboardRelativePathToCompanyStorageReference,
  workboardRelativePathToValuationStorageReference,
} from './workboardPath.utils';

interface WorkboardFolderSyncParams {
  open: boolean;
  valuationId: string | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentCompany: Record<string, any> | undefined | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  companyMeasurementDateById: Record<string, any> | undefined | null;
  isLoadingMeasurementDate: boolean;
  measurementDateError: unknown;
  queryClient: QueryClient;
}

const mapDirectoryToOption = (directory: IWorkboardDirectory): DropboxFolderOption => ({
  name: directory.name,
  pathDisplay: directory.path.endsWith('/') ? directory.path : `${directory.path}/`,
});

const useDropboxFolderSync = ({
  open,
  valuationId,
  currentCompany,
  companyMeasurementDateById,
  isLoadingMeasurementDate,
  measurementDateError,
  queryClient,
}: WorkboardFolderSyncParams) => {
  const { enqueueSnackbar } = useSnackbar();
  const [dropboxRoot, setDropboxRoot] = useState<string>(DEFAULT_DROPBOX_ROOT);
  const [companyFolderStatus, setCompanyFolderStatus] = useState<DropboxFolderStatus>(DROPBOX_FOLDER_STATUS.IDLE);
  const [valuationFolderStatus, setValuationFolderStatus] = useState<DropboxFolderStatus>(DROPBOX_FOLDER_STATUS.IDLE);
  const [companyFolderSuggestions, setCompanyFolderSuggestions] = useState<DropboxFolderOption[]>([]);
  const [valuationFolderSuggestions, setValuationFolderSuggestions] = useState<DropboxFolderOption[]>([]);
  const [companyFolderMatchPaths, setCompanyFolderMatchPaths] = useState<string[]>([]);
  const [valuationFolderMatchPaths, setValuationFolderMatchPaths] = useState<string[]>([]);
  const [companyFolderSimilarMatchesCount, setCompanyFolderSimilarMatchesCount] = useState(0);
  const [valuationFolderSimilarMatchesCount, setValuationFolderSimilarMatchesCount] = useState(0);

  // A linked company folder already lives under one of the valuation roots, so open
  // the selector on that root instead of making the analyst re-pick it.
  const companyStorageReference = currentCompany?.file_storage_reference;
  useEffect(() => {
    if (!open || !companyStorageReference?.trim()) {
      return;
    }
    setDropboxRoot(dropboxRootFromReference(companyStorageReference));
  }, [open, companyStorageReference]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.IDLE);
    setValuationFolderStatus(DROPBOX_FOLDER_STATUS.IDLE);
    setCompanyFolderSuggestions([]);
    setValuationFolderSuggestions([]);
    setCompanyFolderMatchPaths([]);
    setValuationFolderMatchPaths([]);
    setCompanyFolderSimilarMatchesCount(0);
    setValuationFolderSimilarMatchesCount(0);

    // Cheap prefix check only — not a full directory crawl. Skip when no folder is linked yet.
    const companyRef = companyStorageReference?.trim();
    if (!companyRef) {
      return;
    }

    let cancelled = false;
    setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.SEARCHING);

    const verifySavedCompanyFolder = async () => {
      const exists = await verifyBackupFolderExists(companyRef);
      if (cancelled) {
        return;
      }
      if (exists) {
        setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.EXISTS);
        setCompanyFolderMatchPaths([companyRef]);
        setCompanyFolderSimilarMatchesCount(0);
        return;
      }
      setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.NOT_FOUND);
      setCompanyFolderMatchPaths([]);
      setCompanyFolderSimilarMatchesCount(0);
    };

    verifySavedCompanyFolder();

    return () => {
      cancelled = true;
    };
  }, [open, valuationId, companyStorageReference]);

  const dedupeFolderOptions = useCallback((options: DropboxFolderOption[]) => {
    const byPath = new Map<string, DropboxFolderOption>();
    options.forEach(option => {
      if (!option.pathDisplay?.trim()) {
        return;
      }
      if (!byPath.has(option.pathDisplay)) {
        byPath.set(option.pathDisplay, option);
      }
    });
    return Array.from(byPath.values());
  }, []);

  const handleSearchCompanyFolder = useCallback(
    async (folderName: string) => {
      const query = normalizeFolderSegment(folderName);

      setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.SEARCHING);
      try {
        const directories = await listWorkboardDirectories({ prefix: '', q: query, root: dropboxRoot });
        const folderOptions = dedupeFolderOptions(directories.map(mapDirectoryToOption));
        setCompanyFolderSuggestions(current => dedupeFolderOptions([...current, ...folderOptions]));
        if (!query) {
          setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.IDLE);
          setCompanyFolderMatchPaths([]);
          setCompanyFolderSimilarMatchesCount(0);
          return;
        }

        const matchedPaths = Array.from(
          new Set(
            folderOptions
              .filter(option => getDropboxFolderMatchKind(option, query, dropboxRoot) === 'exact')
              .map(option => option.pathDisplay)
              .filter(Boolean)
          )
        );
        const partialMatchCount = folderOptions.filter(
          option => getDropboxFolderMatchKind(option, query, dropboxRoot) === 'partial'
        ).length;
        const hasMatch = matchedPaths.length > 0;
        setCompanyFolderMatchPaths(matchedPaths);
        if (hasMatch) {
          setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.EXISTS);
          setCompanyFolderSimilarMatchesCount(0);
        } else if (partialMatchCount > 0 || folderOptions.length > 0) {
          setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.MATCHES);
          setCompanyFolderSimilarMatchesCount(partialMatchCount || folderOptions.length);
        } else {
          setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.NOT_FOUND);
          setCompanyFolderSimilarMatchesCount(0);
        }
      } catch (err) {
        Logger.error('Error searching company folders in backup storage', err);
        setCompanyFolderMatchPaths([]);
        setCompanyFolderSimilarMatchesCount(0);
        setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.ERROR);
        enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.SEARCH_COMPANY_FOLDER_ERROR, { variant: 'error' });
      }
    },
    [dedupeFolderOptions, dropboxRoot, enqueueSnackbar]
  );

  const handleSearchValuationFolder = useCallback(
    async (folderName: string) => {
      const companyPrefix = toWorkboardRelativePrefixFromStorageReference(
        currentCompany?.file_storage_reference,
        dropboxRoot
      );
      if (!companyPrefix) {
        enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.SET_COMPANY_FOLDER_FIRST, { variant: 'info' });
        return;
      }

      const query = normalizeFolderSegment(folderName);

      setValuationFolderStatus(DROPBOX_FOLDER_STATUS.SEARCHING);
      try {
        const directories = await listWorkboardDirectories({
          prefix: companyPrefix,
          q: query,
          root: dropboxRoot,
        });
        const folderOptions = dedupeFolderOptions(directories.map(mapDirectoryToOption));
        setValuationFolderSuggestions(current => dedupeFolderOptions([...current, ...folderOptions]));
        if (!query) {
          setValuationFolderStatus(DROPBOX_FOLDER_STATUS.IDLE);
          setValuationFolderMatchPaths([]);
          setValuationFolderSimilarMatchesCount(0);
          return;
        }

        const matchedPaths = Array.from(
          new Set(
            folderOptions
              .filter(
                option =>
                  getDropboxFolderMatchKind(option, query, companyPrefix, { relativeToParent: true }) === 'exact'
              )
              .map(option => option.pathDisplay)
              .filter(Boolean)
          )
        );
        const partialMatchCount = folderOptions.filter(
          option =>
            getDropboxFolderMatchKind(option, query, companyPrefix, { relativeToParent: true }) === 'partial'
        ).length;
        const hasMatch = matchedPaths.length > 0;
        setValuationFolderMatchPaths(matchedPaths);
        if (hasMatch) {
          setValuationFolderStatus(DROPBOX_FOLDER_STATUS.EXISTS);
          setValuationFolderSimilarMatchesCount(0);
        } else if (partialMatchCount > 0 || folderOptions.length > 0) {
          setValuationFolderStatus(DROPBOX_FOLDER_STATUS.MATCHES);
          setValuationFolderSimilarMatchesCount(partialMatchCount || folderOptions.length);
        } else {
          setValuationFolderStatus(DROPBOX_FOLDER_STATUS.NOT_FOUND);
          setValuationFolderSimilarMatchesCount(0);
        }
      } catch (err) {
        Logger.error('Error listing valuation folders in backup storage', err);
        setValuationFolderMatchPaths([]);
        setValuationFolderSimilarMatchesCount(0);
        setValuationFolderStatus(DROPBOX_FOLDER_STATUS.ERROR);
        enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.LIST_VALUATION_FOLDER_ERROR, { variant: 'error' });
      }
    },
    [currentCompany?.file_storage_reference, dedupeFolderOptions, dropboxRoot, enqueueSnackbar]
  );

  const handleUpdateCompanyFolder = useCallback(
    async (folderName: string, selectedFolderPath?: string | null, resolvedCompanyId?: number) => {
      const normalizedFolderName = normalizeFolderSegment(folderName);
      if (!normalizedFolderName) {
        enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.ENTER_COMPANY_FOLDER_NAME_SYNC, { variant: 'info' });
        return;
      }
      const companyId = resolvedCompanyId ?? currentCompany?.id;
      if (!companyId) {
        enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.COMPANY_LINK_FAILED, {
          variant: 'error',
          autoHideDuration: 8000,
        });
        Logger.error('Company ID not available after resolve attempt', { currentCompany, resolvedCompanyId });
        return;
      }

      const companyPath = selectedFolderPath
        ? workboardRelativePathToCompanyStorageReference(selectedFolderPath, dropboxRoot)
        : workboardRelativePathToCompanyStorageReference(normalizedFolderName, dropboxRoot);

      setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.SYNCING);
      try {
        const folderExists = await verifyBackupFolderExists(companyPath);
        if (!folderExists) {
          setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.NOT_FOUND);
          setCompanyFolderMatchPaths([]);
          setCompanyFolderSimilarMatchesCount(0);
          enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.FOLDER_NOT_IN_BACKUP, { variant: 'warning' });
          return;
        }
        await ApiService.apiDashboardOperationsCompaniesFileStorageReferencePartialUpdate(String(companyId), {
          file_storage_reference: companyPath,
        });
        await queryClient.invalidateQueries({
          queryKey: [DROPBOX_FOLDER_SYNC_QUERY_KEYS.COMPANY_MEASUREMENT_DATE_BY_ATTIO_VALUATION_ID, valuationId],
        });
        setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.EXISTS);
        setCompanyFolderMatchPaths([companyPath]);
        setCompanyFolderSimilarMatchesCount(0);
        setCompanyFolderSuggestions(current =>
          dedupeFolderOptions([...current, { name: normalizedFolderName, pathDisplay: companyPath }])
        );
        enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.COMPANY_FOLDER_UPDATED, { variant: 'success' });
      } catch (err) {
        Logger.error('Error updating company folder:', err);
        setCompanyFolderMatchPaths([]);
        setCompanyFolderSimilarMatchesCount(0);
        setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.ERROR);
        enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.COMPANY_FOLDER_UPDATE_ERROR, { variant: 'error' });
      }
    },
    [currentCompany, dedupeFolderOptions, dropboxRoot, enqueueSnackbar, queryClient, valuationId]
  );

  const handleUpdateValuationFolder = useCallback(
    async (folderName: string, selectedFolderPath?: string | null) => {
      const normalizedFolderName = normalizeFolderSegment(folderName);
      if (!normalizedFolderName) {
        enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.ENTER_VALUATION_FOLDER_NAME_SYNC, { variant: 'info' });
        return;
      }
      const companyRelativePrefix = toWorkboardRelativePrefixFromStorageReference(
        currentCompany?.file_storage_reference,
        dropboxRoot
      );
      if (!companyRelativePrefix) {
        enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.SET_COMPANY_FOLDER_BEFORE_VALUATION_SYNC, { variant: 'info' });
        return;
      }
      if (isLoadingMeasurementDate) {
        enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.LOADING_MEASUREMENT_DATE, { variant: 'info' });
        return;
      }
      const measurementDateId = companyMeasurementDateById?.id;
      if (!measurementDateId) {
        const errorMessage = measurementDateError
          ? DROPBOX_FOLDER_SYNC_MESSAGES.MEASUREMENT_DATE_NOT_LINKED
          : DROPBOX_FOLDER_SYNC_MESSAGES.MEASUREMENT_DATE_NOT_AVAILABLE;
        enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
        Logger.error('Measurement date ID not available', {
          companyMeasurementDateById,
          valuationId,
          measurementDateError,
        });
        return;
      }

      const valuationPath = selectedFolderPath
        ? workboardRelativePathToValuationStorageReference(selectedFolderPath, dropboxRoot)
        : workboardRelativePathToValuationStorageReference(
            `${companyRelativePrefix}${normalizedFolderName}`,
            dropboxRoot
        );
      const cmdReference = normalizeCmdFileStorageReference(
        selectedFolderPath
          ? valuationFolderNameFromWorkboardPath(selectedFolderPath, companyRelativePrefix)
          : normalizedFolderName
      );

      setValuationFolderStatus(DROPBOX_FOLDER_STATUS.SYNCING);
      try {
        const folderExists = await verifyBackupFolderExists(valuationPath);
        if (!folderExists) {
          setValuationFolderStatus(DROPBOX_FOLDER_STATUS.NOT_FOUND);
          setValuationFolderMatchPaths([]);
          setValuationFolderSimilarMatchesCount(0);
          enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.FOLDER_NOT_IN_BACKUP, { variant: 'warning' });
          return;
        }
        await ApiService.apiDashboardOperationsCompanyMeasurementDatesFileStorageReferencePartialUpdate(
          String(measurementDateId),
          { file_storage_reference: cmdReference }
        );
        await queryClient.invalidateQueries({
          queryKey: [DROPBOX_FOLDER_SYNC_QUERY_KEYS.COMPANY_MEASUREMENT_DATE_BY_ATTIO_VALUATION_ID, valuationId],
        });
        setValuationFolderStatus(DROPBOX_FOLDER_STATUS.EXISTS);
        setValuationFolderMatchPaths([valuationPath]);
        setValuationFolderSimilarMatchesCount(0);
        setValuationFolderSuggestions(current =>
          dedupeFolderOptions([...current, { name: normalizedFolderName, pathDisplay: valuationPath }])
        );
        enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.VALUATION_FOLDER_UPDATED, { variant: 'success' });
      } catch (err) {
        Logger.error('Error updating valuation folder:', err);
        setValuationFolderMatchPaths([]);
        setValuationFolderSimilarMatchesCount(0);
        setValuationFolderStatus(DROPBOX_FOLDER_STATUS.ERROR);
        enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.VALUATION_FOLDER_UPDATE_ERROR, { variant: 'error' });
      }
    },
    [
      companyMeasurementDateById,
      currentCompany?.file_storage_reference,
      dedupeFolderOptions,
      dropboxRoot,
      enqueueSnackbar,
      isLoadingMeasurementDate,
      measurementDateError,
      queryClient,
      valuationId,
    ]
  );

  return {
    dropboxRoot,
    setDropboxRoot,
    companyFolderStatus,
    valuationFolderStatus,
    companyFolderSuggestions,
    valuationFolderSuggestions,
    companyFolderMatchPaths,
    valuationFolderMatchPaths,
    companyFolderSimilarMatchesCount,
    valuationFolderSimilarMatchesCount,
    handleSearchCompanyFolder,
    handleSearchValuationFolder,
    handleUpdateCompanyFolder,
    handleUpdateValuationFolder,
  };
};

export default useDropboxFolderSync;
